"use client";

import RouterLink from "@/components/ui/router-link";
import Swiper, { SwiperSlide } from "@/components/ui/swiper";

import { useScopedI18n } from "@/messages/client";
import { ChevronRight, Send } from "lucide-react";
import { ProductApplication } from "./types";
import { DirectusImage } from "@/components/DirectusImage";
import { useSite } from "@/hooks/useSiteInfo";

import { format } from "date-fns";
import { useCallback } from "react";
import { Button } from "@/components/ui/button";
import useLangData from "@/hooks/useLangData";

export default function Application(props: {
  data?: ProductApplication[];
  applicationSlug?: string;
}) {
  const t = useScopedI18n("产品");

  const site = useSite();

  const getLangData = useLangData();

  // 计算发布时间
  const getPublishDate = useCallback(
    (data: ProductApplication) => {
      const publish = data.publish.map((item) => item.publish_id);

      return publish.find((item) => item.site === site)?.publish_date ?? "";
    },
    [site]
  );

  if (!props.data?.length) {
    return <div className="md:pt-[120px] pt-10"></div>;
  }

  return (
    <div className="relative pb-10 pt-10 md:pb-[90px] md:pt-[120px]">
      <div
        id="application-solutions"
        className="pointer-events-none absolute -top-[46px]"></div>
      <div className="flex w-full justify-center md:items-end md:justify-between">
        <h2 className="text-xl md:text-4xl">{t("应用方案")}</h2>

        {props.applicationSlug && (
          <RouterLink
            className="hidden items-center space-x-2 text-base text-[#808080] md:flex"
            href={`/application-category/${props.applicationSlug}`}>
            <span>{t("更多应用方案")}</span>

            <Send size={16} />
          </RouterLink>
        )}
      </div>

      <div className="mt-5 md:mt-12">
        <Swiper
          className="box-content h-[350px] w-full md:!-ml-2 md:h-[430px] md:!p-2"
          slidesPerView={1}
          pagination={{
            clickable: true,
            bulletClass: "swiper-pagination-bullet cases-bullet",
            bulletActiveClass:
              "swiper-pagination-bullet-active cases-bullet-active",
          }}
          breakpoints={{
            1024: {
              slidesPerView: 3,
              spaceBetween: 18,
            },
            768: {
              slidesPerView: 2,
              spaceBetween: 18,
            },
          }}>
          {props.data?.map?.((item) => (
            <SwiperSlide key={item.slug}>
              <RouterLink
                href={`/application/${item.slug}`}
                // md:shadow-none shadow-[0px_0px_12px_rgba(0,0,0,0.1)]
                className="group relative flex h-[320px] w-full flex-col overflow-hidden rounded-md border border-[#F2F5F4] bg-white transition-all duration-300 hover:shadow-[0px_4px_16px_rgba(0,0,0,0.1)] md:h-[400px]">
                <div className="relative h-[183px] w-full overflow-hidden bg-slate-300 md:h-[217px]">
                  {item.image && (
                    <DirectusImage
                      zoomInGroup
                      className="object-cover object-center"
                      id={item.image}
                      alt={item.name}
                      fill
                    />
                  )}
                </div>

                <div className="flex flex-1 flex-col px-4 pb-4 pt-3 md:px-7 md:pb-9 md:pt-[18px]">
                  <div className="flex flex-1 flex-col justify-between bg-[url('/mark.webp')] bg-left-top bg-no-repeat pl-2 pt-4 md:pl-5">
                    <div className="line-clamp-3 text-justify text-sm text-[#2C2C2C]">
                      {getLangData(
                        "description",
                        item.description,
                        item.lang_data
                      )}
                    </div>

                    <div className="flex items-center justify-between text-xs md:text-sm">
                      <time className="text-[#A6A6A6]">
                        {format(new Date(getPublishDate(item)), "yyyy.MM.dd")}
                      </time>

                      <div className="flex items-center space-x-1 text-[#808080]">
                        <span>{t("前往了解更多")}</span>
                        <ChevronRight size={16} />
                      </div>
                    </div>
                  </div>
                </div>
              </RouterLink>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      <Button
        variant="outline"
        className="mt-5 h-[46px] w-full space-x-2 rounded-lg text-sm md:hidden">
        <span>{t("前往了解更多")}</span>
        <Send size={14} />
      </Button>
    </div>
  );
}
